// Name → component map for the generative-UI canvas (TASK-493, ports SixEase
// registry.ts). `WidgetRenderer` looks names up here; a miss → FallbackCard.
// Keys ARE the catalog the UI brain (TASK-499) allowlists — keep them exact.
import type { ComponentType } from 'react';
import ChangeRadar from '../components/dashboard/ChangeRadar';
import {
  DnaCardWidget,
  NotesTabWidget,
  DocumentsTabWidget,
  MinIODocumentsWidget,
  PortfoliosTabWidget,
  ProposalsListWidget,
  CockpitPortfolioWidget,
  COReviewQueueWidget,
  COReviewWorkspaceWidget,
  DocumentComplianceWidget,
  ComplianceStatusWidget,
  QuarantineQueueWidget,
  RegulatoryFeedWidget,
  SecureLinksTableWidget,
  DocumentRequestListWidget,
  AllDocumentsWidget,
  DocumentAnalysisWidget,
  DocumentViewerWidget,
  ActivityTimelineWidget,
  AIUsageStatsWidget,
  VoiceNoteTile,
  KYCTabWidget,
  RiskProfileTabWidget,
  MeetingActionItemsWidget,
} from '../components/canvas/widgets';
// TASK-576 — life events as a first-class canvas widget (own file: facet chips).
import { LifeEventsWidget } from '../components/canvas/widgets/LifeEventsWidget';
import { NewProspect } from '../components/canvas/widgets/NewProspect';
import { OnboardingChecklist } from '../components/canvas/widgets/OnboardingChecklist';
// TASK-495 filterable widgets live in their own files (bigger than the thin adapters).
import { ClientBook } from '../components/canvas/widgets/ClientBook';
// TASK-666 — the account's meetings as a canvas list widget (replaces the /meetings page).
import { MeetingsList } from '../components/canvas/widgets/MeetingsList';
// TASK-808 — the mailbox Kielis already indexes, inbound counterpart to EmailDraft.
import { EmailList } from '../components/canvas/widgets/EmailList';
// TASK-667 — pushed monitor tile for bot-attended meetings (never starts a microphone).
import { LiveMeetingNow } from '../components/canvas/widgets/LiveMeetingNow';
import { MeetingRecorderWidget } from '../components/canvas/widgets/MeetingRecorderWidget';
import { PostMeetingReviewWidget } from '../components/canvas/widgets/PostMeetingReviewWidget';
import { ClientLevelWidget } from '../components/canvas/widgets/ClientLevelWidget';
// TASK-496 SixEase-only ports — self-fetching, own WidgetContainer, own files.
import { MeetingPrep } from '../components/canvas/widgets/MeetingPrep';
import { DriftBars } from '../components/canvas/widgets/DriftBars';
// TASK-901 — the trade simulation the canvas could only refuse.
import { TradeDeltaPanel } from '../components/canvas/widgets/TradeDelta';
import { AllocationDonut } from '../components/canvas/widgets/AllocationDonut';
import { SuitabilityCard } from '../components/canvas/widgets/SuitabilityCard';
import { RiskProfileSummary } from '../components/canvas/widgets/RiskProfileSummary';
import { RiskAnalytics } from '../components/canvas/widgets/RiskAnalytics';
import { RiskCommentary } from '../components/canvas/widgets/RiskCommentary';
// TASK-580 — filterable narrative client-profile summary (client-scoped, self-fetch).
import { ClientProfileSummary } from '../components/canvas/widgets/ClientProfileSummary';
// TASK-581 — grounded portfolio narrative + flag chips (client-scoped, self-fetch).
import { PortfolioCommentary } from '../components/canvas/widgets/PortfolioCommentary';
import { ResearchBrief, IndustryDossier } from '../components/canvas/widgets/ResearchDossier';
import { MorningDigest } from '../components/canvas/widgets/MorningDigest';
// TASK-583 — compliance cockpit (3-domain summary) + deep document review (client-scoped).
import { ComplianceCockpit } from '../components/canvas/widgets/ComplianceCockpit';
import { ComplianceReview } from '../components/canvas/widgets/ComplianceReview';
import { ConflictsList } from '../components/canvas/widgets/ConflictsList';
import { Client360 } from '../components/canvas/widgets/Client360';
import { SectorTreemap } from '../components/canvas/widgets/SectorTreemap';
import { EmailDraft } from '../components/canvas/widgets/EmailDraft';
import { BeforeAfter } from '../components/canvas/widgets/BeforeAfter';
// TASK-831: the house view an advisor is asked to act on, readable.
import { CIOHouseView } from '../components/canvas/widgets/CIOHouseView';
// TASK-830: why a holding conflicts, and what to hold instead.
import { DnaConflict } from '../components/canvas/widgets/DnaConflict';
import { SignalDossier } from '../components/canvas/widgets/SignalDossier';
// TASK-499 — conversation bubble; rendered bare (full-width, no tile chrome) by Canvas.
import { TasksAwaitingReview } from '../components/canvas/widgets/TasksAwaitingReview';
import { ArtifactRepairQueue } from '../components/canvas/widgets/ArtifactRepairQueue';
import { ClientReaction } from '../components/canvas/widgets/ClientReaction';
import { RiskTimeline } from '../components/canvas/widgets/RiskTimeline';
import { KnowledgeGaps } from '../components/canvas/widgets/KnowledgeGaps';
import { CountryExposure } from '../components/canvas/widgets/CountryExposure';
import { PositionsTablePanel } from '../components/canvas/widgets/PositionsTable';
import { IssuerConcentrationPanel } from '../components/canvas/widgets/IssuerConcentration';
import { SecurityExposurePanel } from '../components/canvas/widgets/SecurityExposure';
import { MaturityLadderPanel } from '../components/canvas/widgets/MaturityLadder';
import { ChatMessage } from '../components/canvas/widgets/ChatMessage';
// TASK-742 — creation-result card (orchestrator-emitted; not brain-summonable).
import { ArtifactCard } from '../components/canvas/widgets/ArtifactCard';
import { ARTIFACT_CARD_COMPONENT } from '../hooks/useAgenticChat';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const registry = new Map<string, ComponentType<any>>([
  ['ChangeRadar', ChangeRadar],
  // TASK-494 Tier-1 widgets — adapters wrap each in WidgetContainer for the
  // provenance badge. Client-scoped ones receive only { clientId }.
  ['DnaCard', DnaCardWidget],
  ['NotesTab', NotesTabWidget],
  ['DocumentsTab', DocumentsTabWidget],
  ['MinIODocumentsSection', MinIODocumentsWidget],
  ['PortfoliosTab', PortfoliosTabWidget],
  ['ProposalsList', ProposalsListWidget],
  ['CockpitPortfolioView', CockpitPortfolioWidget],
  ['COReviewQueue', COReviewQueueWidget],
  // TASK-733: opened by COReviewQueue's canvas selection (needs a reviewId the brain
  // cannot resolve, so not NL-summonable — documented in test_ui_router.py).
  ['COReviewWorkspace', COReviewWorkspaceWidget],
  ['DocumentComplianceDashboard', DocumentComplianceWidget],
  // TASK-555 — account-level honest compliance status (book-level, self-fetch).
  ['ComplianceStatus', ComplianceStatusWidget],
  ['QuarantineQueue', QuarantineQueueWidget],
  ['RegulatoryFeedTab', RegulatoryFeedWidget],
  // TASK-507 — additional self-fetch widgets (client: id-injected; book: no props).
  ['SecureLinksTable', SecureLinksTableWidget],
  ['DocumentRequestList', DocumentRequestListWidget],
  ['AllDocumentsView', AllDocumentsWidget],
  ['DocumentAnalysisTab', DocumentAnalysisWidget],
  // TASK-511 — canonical PDF viewer + timeline (client-scoped, self-fetch).
  ['DocumentViewerPanel', DocumentViewerWidget],
  ['ActivityTimeline', ActivityTimelineWidget],
  // TASK-495 filterable widgets: book/list + client-level identity.
  ['ClientBook', ClientBook],
  // TASK-666 — book-level meetings list (never receives a clientId).
  ['MeetingsList', MeetingsList],
  // TASK-808 — received mail. Client-OPTIONAL like MeetingsList: "show my emails" must never
  // demand a client, "show Schumacher's emails" narrows to one. Not to be confused with
  // EmailDraft, which composes outbound.
  ['EmailList', EmailList],
  // TASK-667 — auto-surfaced live monitor; one tile per live session.
  ['LiveMeetingNow', LiveMeetingNow],
  // TASK-673 — in-person silent recorder as a canvas tile (capture stays on the canvas).
  ['MeetingRecorderWidget', MeetingRecorderWidget],
  // TASK-673 — post-meeting review as a canvas tile (the last route-out in the flow).
  ['PostMeetingReviewWidget', PostMeetingReviewWidget],
  ['ClientLevelView', ClientLevelWidget],
  // TASK-496 SixEase-only ports (client-scoped: receive { clientId }).
  ['MeetingPrep', MeetingPrep],
  ['DriftBars', DriftBars],
  // TASK-901 — sizes a breached slot back to target out of positions actually held,
  // and says plainly when the trade opens a new breach elsewhere.
  ['TradeDelta', TradeDeltaPanel],
  ['AllocationDonut', AllocationDonut],
  ['ConflictsList', ConflictsList],
  ['Client360', Client360],
  ['SectorTreemap', SectorTreemap],
  ['EmailDraft', EmailDraft],
  ['BeforeAfter', BeforeAfter],
  ['CIOHouseView', CIOHouseView],  // TASK-831
  ['DnaConflict', DnaConflict],  // TASK-830
  ['SignalDossier', SignalDossier],  // TASK-833 — the whole case, summoned by Investigate
  // TASK-512 — bucket A (self-fetch, no extra props) + the clientName-scope tabs.
  // AIUsageStats is book-level (no clientId); the rest are client-scoped.
  ['AIUsageStats', AIUsageStatsWidget],
  ['VoiceNoteWidget', VoiceNoteTile],
  ['KYCTab', KYCTabWidget],
  ['RiskProfileTab', RiskProfileTabWidget],
  // TASK-579 — compact suitability verdict for one proposal (self-fetch by client+proposal).
  ['SuitabilityCard', SuitabilityCard],
  // TASK-579 — compact 3-dimension risk summary (capacity/tolerance/required + goal gap).
  ['RiskProfileSummary', RiskProfileSummary],
  // TASK-582 — live portfolio risk analytics (VaR/vol/stress), self-fetch from clientId.
  ['RiskAnalytics', RiskAnalytics],
  // TASK-582 honest-scope — persisted AI risk read-out (narrative + flags + delta).
  ['RiskCommentary', RiskCommentary],
  // TASK-580 — narrative client-profile summary, filterable by section.
  ['ClientProfileSummary', ClientProfileSummary],
  // TASK-581 — grounded portfolio analysis: narrative + suitability/ESG/concentration chips.
  ['PortfolioCommentary', PortfolioCommentary],
  // TASK-612 — cited research dossiers with computed book strip (self-fetch by subject).
  ['ResearchBrief', ResearchBrief],
  ['IndustryDossier', IndustryDossier],
  // TASK-611 — overnight signal × holdings × client-constraints triage (Today surface).
  ['MorningDigest', MorningDigest],
  // TASK-583 — compliance cockpit (Review/Suitability/AML summary) + deep document review.
  ['ComplianceCockpit', ComplianceCockpit],
  ['ComplianceReview', ComplianceReview],
  // TASK-575 redesign — meeting action items / follow-ups (client-scoped, self-fetch).
  ['MeetingActionItems', MeetingActionItemsWidget],
  // TASK-576 — first-class Life-Events widget (client-scoped, self-fetch from KG).
  ['LifeEvents', LifeEventsWidget],
  // TASK-659 — generative prospect intake + resumable onboarding checklist.
  ['NewProspect', NewProspect],
  ['OnboardingChecklist', OnboardingChecklist],
  // TASK-685 — what the AI finished and left for sign-off (book-level, self-fetch).
  ['TasksAwaitingReview', TasksAwaitingReview],
  ['ArtifactRepairQueue', ArtifactRepairQueue],
  // TASK-687 — pre-call pre-mortem: how this client will react, quoting what they said.
  ['ClientReaction', ClientReaction],
  // TASK-688 — documented appetite over time vs the mandate band, every point quoted.
  ['RiskTimeline', RiskTimeline],
  // TASK-689 — what the file is missing, with a question to ask for each.
  ['KnowledgeGaps', KnowledgeGaps],
  // TASK-690 — the geographic lens (no globe): exposure by country with its resolution basis.
  ['CountryExposure', CountryExposure],
  // TASK-908: the plainest question an advisor asks — largest positions with CHF
  // market VALUES. Both catalogued prompts landed on the cockpit's weights.
  ['PositionsTable', PositionsTablePanel],
  // TASK-906: the book screened by issuer, replacing a roster titled with a
  // threshold it had never applied.
  ['IssuerConcentration', IssuerConcentrationPanel],
  // TASK-904: one security across the book, with the RM's own shock.
  ['SecurityExposure', SecurityExposurePanel],
  // TASK-907: bonds by redemption year, and the coupon they project.
  ['MaturityLadder', MaturityLadderPanel],
  // TASK-499 — dialogue turns render inline in the canvas alongside summoned widgets.
  ['ChatMessage', ChatMessage],
  // TASK-742 — a generated/templated document as a reviewable card (chat creation no
  // longer navigates; only explicit open_document does). Emitted by the orchestrator.
  [ARTIFACT_CARD_COMPONENT, ArtifactCard],
]);
